import { Tracker } from '../../domains/analytics/services/Tracker';

type Cleanup = () => void;

export const setupDataAttributeTracking = (): Cleanup => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }
  const isTest = window.location.search.includes('test=true');

  const parseEventParams = (raw: string | null) => {
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch (error) {
      console.error('Failed to parse data-event-params JSON', error);
      return undefined;
    }
  };

  const handleTrackedClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-event-click]') : null;
    if (!target) return;
    const eventName = target.getAttribute('data-event-click');
    if (!eventName) return;
    const params = parseEventParams(target.getAttribute('data-event-params'));
    Tracker.track(eventName, params);
    if (isTest) {
      const tracked = (window as unknown as { __trackedEvents?: Array<{ name: string; params?: Record<string, unknown> }> }).__trackedEvents ?? [];
      tracked.push({ name: eventName, params });
      (window as unknown as { __trackedEvents?: Array<{ name: string; params?: Record<string, unknown> }> }).__trackedEvents = tracked;
    }
  };

  document.addEventListener('click', handleTrackedClick);

  const trackedLoadElements = new WeakSet<HTMLElement>();
  const handleElementLoad = (element: HTMLElement | null) => {
    if (!element || trackedLoadElements.has(element)) return;
    const eventName = element.getAttribute('data-event-load');
    if (!eventName) return;
    trackedLoadElements.add(element);
    const params = parseEventParams(element.getAttribute('data-event-params'));
    Tracker.track(eventName, params);
  };

  const scanForLoadElements = (root: ParentNode | null) => {
    if (!root) return;
    if (root instanceof HTMLElement && root.matches('[data-event-load]')) {
      handleElementLoad(root);
    }
    const nodes = root.querySelectorAll?.('[data-event-load]');
    nodes?.forEach((node) => {
      if (node instanceof HTMLElement) {
        handleElementLoad(node);
      }
    });
  };

  scanForLoadElements(document.body);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
        if (mutation.attributeName === 'data-event-load') {
          handleElementLoad(mutation.target);
        }
      }
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            handleElementLoad(node);
            scanForLoadElements(node);
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-event-load'],
  });

  return () => {
    document.removeEventListener('click', handleTrackedClick);
    observer.disconnect();
  };
};
